import React, { useState } from 'react';
import { ArrowLeft, Pencil, Save, Trash2, ExternalLink } from 'lucide-react';
import * as Icons from 'lucide-react';
import { socialLinks } from '../mockdata/Links';
import styles from '../CSS/Links.module.css';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLinks } from '../../../../hooks/admin/useAdminLinks';

const Links = () => {
  const navigate = useNavigate();
  const { links, isLoading, error, updateLink, clearLink } = useLinks();

  // Tracks which link is in edit mode
  const [editStates, setEditStates] = useState({});
  // Stores temporary edited values
  const [tempValues, setTempValues] = useState({});

  // Combine backend data with social links template
  const enrichedLinks = socialLinks.map(templateLink => {
    const userLink = links?.find(link => link.platform === templateLink.id);
    return {
      ...templateLink,
      url: userLink?.url || ''
    };
  });

  // Validation: if empty, allow it; if not, validate based on type
  const validateValue = (value, id) => {
    if (value === '') return true;
    if (id === 'gmail') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    } else {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }
  };

  const handleEdit = (id) => {
    setEditStates(prev => ({ ...prev, [id]: true }));
    setTempValues(prev => ({
      ...prev,
      [id]: enrichedLinks.find(link => link.id === id).url
    }));
  };

  const handleSave = async (id) => {
    const newValue = tempValues[id];
    if (validateValue(newValue, id)) {
      try {
        await updateLink({ platform: id, url: newValue });
        setEditStates(prev => ({ ...prev, [id]: false }));
        toast.success('Link updated successfully');
      } catch (error) {
        toast.error('Failed to update link');
      }
    } else {
      toast.error(
        id === 'gmail'
          ? 'Please enter a valid email address'
          : 'Please enter a valid URL'
      );
    }
  };

  const handleClear = async (id) => {
    try {
      await clearLink(id);
      setTempValues(prev => ({ ...prev, [id]: '' }));
      toast.success('Link cleared successfully');
    } catch (error) {
      toast.error('Failed to clear link');
    }
  };

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerSection}>
        <div className={styles.headerContent}>
          <div className={styles.headerTop}>
            <h1 className={styles.title}>Social Media Links</h1>
            <button
              className={styles.backButton}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
          </div>
        </div>
      </div>

      <div className={styles.mainContainer}>
        <div className={styles.grid}>
          {enrichedLinks.map(link => {
            const IconComponent = Icons[link.icon];
            const isEditing = editStates[link.id] || false;
            return (
              <div key={link.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <IconComponent size={24} className={styles.icon} />
                  <h2 className={styles.platformName}>{link.name}</h2>
                </div>

                <div className={styles.cardContent}>
                  {isEditing ? (
                    <input
                      type={link.id === 'gmail' ? 'email' : 'url'}
                      value={tempValues[link.id] || ''}
                      className={styles.urlInput}
                      onChange={(e) =>
                        setTempValues(prev => ({
                          ...prev,
                          [link.id]: e.target.value
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSave(link.id);
                        }
                      }}
                      placeholder={
                        link.id === 'gmail'
                          ? 'Enter your email'
                          : 'Enter URL'
                      }
                    />
                  ) : link.id === 'gmail' ? (
                    <span className={styles.url}>{link.url}</span>
                  ) : (
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.url}
                    >
                      {link.url}
                      {link.url && <ExternalLink size={16} />}
                    </a>
                  )}
                </div>

                <div className={styles.cardActions}>
                  <button
                    className={styles.editButton}
                    onClick={() =>
                      isEditing ? handleSave(link.id) : handleEdit(link.id)
                    }
                  >
                    {isEditing ? <Save size={20} /> : <Pencil size={20} />}
                    <span>{isEditing ? 'Save' : 'Edit'}</span>
                  </button>
                  <button
                    className={styles.resetButton}
                    onClick={() => handleClear(link.id)}
                    disabled={!link.url}
                  >
                    <Trash2 size={20} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Links;